import {
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MediaTypes } from '@videoblocks/jelly-renderer';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { CommonFilters, ImageFilters } from '../../constants/SearchFilters';
import { trackEvent } from '../../events/sendEvents';
import { SIDE_DRAWER } from '../../events/tags';
import {
  changeContentType,
  changeFilters,
  removeFilter,
  resetContentType,
  selectContentType,
  selectDrawer,
  selectFilters,
  selectKeywords,
} from '../../features/assetDrawer/assetDrawerSlice';
import useImageCategories from '../../hooks/useImageCategories';
import { findLabel } from '../../utils/searchUtils';

const useStyles = makeStyles((theme) => ({
  grid: {
    paddingBottom: theme.spacing(1),
  },
  chips: {
    '& > * + *': {
      marginLeft: theme.spacing(1),
    },
  },
}));

export default function ImageSearchFilters(props) {
  const dispatch = useDispatch();
  const contentType = useSelector(selectContentType);
  const drawer = useSelector(selectDrawer);
  const filters = useSelector(selectFilters);
  const keywords = useSelector(selectKeywords);

  const { editable } = props;
  const { category = '', orientation = '' } = filters;
  const {
    categories = [],
    contentTypes,
    orientationOptions,
  } = useImageCategories();
  const classes = useStyles();
  const mediaType = MediaTypes.IMAGE;

  const updateFilter = (filterName) => (event) => {
    const oldValue = filters[filterName];
    const filterValue = event.target.value.toString();
    dispatch(changeFilters({ ...filters, [filterName]: filterValue }));
    if (filterValue !== oldValue) {
      trackEvent(SIDE_DRAWER.FILTER_SELECT, {
        drawer,
        filterName,
        filterValue,
        mediaType,
      });
      trackEvent(SIDE_DRAWER.SEARCH_FILTERS, {
        text: keywords,
        ...filters,
        [filterName]: filterValue,
        contentType,
        mediaType,
        drawer,
      });
    }
  };

  const updateContentType = (event) => {
    const oldValue = contentType;
    const filterValue = event.target.value.toString();
    dispatch(changeContentType(filterValue));
    if (filterValue !== oldValue) {
      trackEvent(SIDE_DRAWER.FILTER_SELECT, {
        drawer,
        filterName: 'contentType',
        filterValue,
        mediaType,
      });
      trackEvent(SIDE_DRAWER.SEARCH_FILTERS, {
        text: keywords,
        ...filters,
        //contentType has not been updated to the filterValue at this point
        contentType: filterValue,
        mediaType,
        drawer,
      });
    }
  };

  const removeFilterChip = (filterName) => {
    const oldValue = filters[filterName];
    dispatch(removeFilter(filterName));
    trackEvent(SIDE_DRAWER.FILTER_REMOVE, {
      drawer,
      filterName,
      filterValue: oldValue,
      mediaType,
    });
  };

  const removeContentTypeChip = () => {
    const oldValue = contentType;
    dispatch(resetContentType());
    trackEvent(SIDE_DRAWER.FILTER_REMOVE, {
      drawer,
      filterName: 'contentType',
      filterValue: oldValue,
      mediaType,
    });
  };

  const trackFilterOpen = (filterName) => () => {
    trackEvent(SIDE_DRAWER.FILTER_OPEN, {
      drawer,
      filterName,
      filterValue: filters[filterName] || '',
      mediaType,
    });
  };

  const trackContentTypeOpen = () => {
    trackEvent(SIDE_DRAWER.FILTER_OPEN, {
      drawer,
      filterName: 'contentType',
      filterValue: contentType || '',
      mediaType,
    });
  };

  if (!editable) {
    if (!contentType && !category) {
      return null;
    }
    return (
      <div className={classes.chips}>
        {contentType && (
          <Chip
            label={findLabel(contentTypes, contentType)}
            onDelete={() => removeContentTypeChip()}
          />
        )}
        {category && (
          <Chip
            label={findLabel(categories, category)}
            onDelete={() => removeFilterChip(CommonFilters.CATEGORY)}
          />
        )}
        {orientation && (
          <Chip
            label={findLabel(orientationOptions, orientation)}
            onDelete={() => removeFilterChip(ImageFilters.ORIENTATION)}
          />
        )}
      </div>
    );
  }

  return (
    <Grid className={classes.grid} container spacing={1}>
      <Grid item xs={6}>
        <InputLabel id="media-type-label">Media Type</InputLabel>
        <FormControl variant="outlined" fullWidth>
          <Select
            labelId="media-type-label"
            color="secondary"
            displayEmpty
            onChange={updateContentType}
            value={contentType}
            onOpen={trackContentTypeOpen}
          >
            <MenuItem value="">All</MenuItem>
            {contentTypes.map(({ id, name }) => (
              <MenuItem value={id} key={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <InputLabel id="category-label">Category</InputLabel>
        <FormControl variant="outlined" fullWidth>
          <Select
            labelId="category-label"
            color="secondary"
            displayEmpty
            onChange={updateFilter(CommonFilters.CATEGORY)}
            value={category}
            onOpen={trackFilterOpen(CommonFilters.CATEGORY)}
          >
            <MenuItem value="">None</MenuItem>
            {categories.map(({ id, name }) => (
              <MenuItem value={id} key={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <InputLabel id="orientation-label">Orientation</InputLabel>
        <FormControl variant="outlined" fullWidth>
          <Select
            labelId="orientation-label"
            color="secondary"
            displayEmpty
            onChange={updateFilter(ImageFilters.ORIENTATION)}
            value={orientation}
            onOpen={trackFilterOpen(ImageFilters.ORIENTATION)}
          >
            <MenuItem value="">None</MenuItem>
            {orientationOptions.map(({ id, name }) => (
              <MenuItem value={id} key={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
}

ImageSearchFilters.propTypes = {
  editable: PropTypes.bool,
};
